<script setup lang="ts">
/**
 * 应用外壳：全局提示 + 空闲保护 + 全屏按钮。
 *
 * 空闲保护放在最外层，因为它是「整台机器」的行为，不属于某一页：
 * 顾客在点餐页、确认页都会超时，而付款页和取餐页**不能**被清掉
 * （一个正在付钱，一个正在等叫号）。
 */
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import NoticeToast from './components/NoticeToast.vue';
import IdleDialog from './components/IdleDialog.vue';
import { kiosk } from './stores/kiosk';
import { countdownSeconds, evaluateIdle } from './session/idle';

const route = useRoute();
const router = useRouter();

/**
 * 是不是「顾客用自己手机扫桌码」进来的。
 * 这种模式下空闲清空和全屏按钮都不该有 —— 那是店里公用一体机才需要的。
 * 顾客在自己手机上看到「你还在吗？」然后被清空，会一头雾水。
 */
const isTableMode = computed(() => kiosk.state.config.mode === 'table');

/** 只有这两页会被空闲保护清空 */
const guarded = computed(
  () => !isTableMode.value && (route.name === 'menu' || route.name === 'confirm'),
);

const showIdle = ref(false);
const idleLeft = ref(0);
let lastTouch = Date.now();
let timer: ReturnType<typeof setInterval> | null = null;

function markTouch(): void {
  lastTouch = Date.now();
  if (showIdle.value) {
    showIdle.value = false;
    // 顾客碰了屏幕 = 还要继续点，把空闲计时重新开始
  }
}

function onVisibility(): void {
  // 页面切回来时不要立刻弹「还在吗」（时间不是顾客在看屏幕）
  lastTouch = Date.now();
  showIdle.value = false;
}

function resetToHome(): void {
  showIdle.value = false;
  kiosk.resetToIdle();
  if (route.name !== 'welcome') void router.push('/');
}

onMounted(() => {
  // 触屏机上 pointerdown 就够；keydown 是为了用鼠标/键盘调试时也能用
  window.addEventListener('pointerdown', markTouch, { passive: true });
  window.addEventListener('keydown', markTouch);
  document.addEventListener('visibilitychange', onVisibility);

  timer = setInterval(() => {
    if (!guarded.value || showIdle.value) {
      // 弹窗已经出来了，就只更新倒计时
      if (showIdle.value) {
        const seconds = Math.round((Date.now() - lastTouch) / 1000);
        idleLeft.value = countdownSeconds(
          seconds,
          kiosk.state.config.idleWarnSeconds,
          kiosk.state.config.idleResetSeconds,
        );
        if (idleLeft.value <= 0) resetToHome();
      }
      return;
    }
    const seconds = Math.round((Date.now() - lastTouch) / 1000);
    const action = evaluateIdle(
      seconds,
      kiosk.state.config.idleWarnSeconds,
      kiosk.state.config.idleResetSeconds,
    );
    if (action === 'warn' || action === 'reset') {
      idleLeft.value = countdownSeconds(
        seconds,
        kiosk.state.config.idleWarnSeconds,
        kiosk.state.config.idleResetSeconds,
      );
      showIdle.value = true;
    }
  }, 1000);
});

onBeforeUnmount(() => {
  window.removeEventListener('pointerdown', markTouch);
  window.removeEventListener('keydown', markTouch);
  document.removeEventListener('visibilitychange', onVisibility);
  if (timer) clearInterval(timer);
});

/** 全屏（触屏机首次部署时店员点一下就能进 kiosk 全屏） */
function toggleFullscreen(): void {
  if (document.fullscreenElement) void document.exitFullscreen();
  else void document.documentElement.requestFullscreen?.();
}
</script>

<template>
  <div class="shell">
    <router-view />

    <!-- 全屏按钮放在角落，店员用得到，顾客基本不会碰到 -->
    <button
      v-if="!isTableMode"
      class="shell__fullscreen"
      title="全屏"
      @click="toggleFullscreen"
    >
      ⛶
    </button>

    <IdleDialog v-if="showIdle" :seconds="idleLeft" @stay="markTouch" @reset="resetToHome" />

    <NoticeToast
      v-if="kiosk.state.notice"
      :text="kiosk.state.notice.text"
      :type="kiosk.state.notice.type"
      @close="kiosk.setNotice(null)"
    />
  </div>
</template>

<style scoped>
.shell {
  height: 100%;
  position: relative;
}

.shell__fullscreen {
  position: fixed;
  right: 10px;
  bottom: 8px;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  color: var(--muted);
  opacity: 0.35;
  font-size: 20px;
  z-index: 40;
}

.shell__fullscreen:active {
  opacity: 0.9;
}
</style>
